
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { KnowledgeBaseEntry } from "@/types/theme";
import { useToast } from "@/components/ui/use-toast";

export const useKnowledgeBaseMutations = (chatbotId: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addKnowledgeBaseEntry = useMutation({
    mutationFn: async (entry: Omit<KnowledgeBaseEntry, 'id' | 'chatbot_id' | 'created_at' | 'updated_at'>) => {
      if (!chatbotId) throw new Error("No chatbot ID provided");
      
      const { error } = await supabase
        .from("knowledge_base")
        .insert([{
          chatbot_id: chatbotId,
          title: entry.title,
          content: entry.content
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge_base", chatbotId] });
      toast({
        title: "Entry added",
        description: "Knowledge base entry has been added successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateKnowledgeBaseEntry = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<KnowledgeBaseEntry> }) => {
      if (!chatbotId) throw new Error("No chatbot ID provided");
      
      const dbUpdates: any = {};
      
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.content !== undefined) dbUpdates.content = updates.content;

      const { error } = await supabase
        .from("knowledge_base")
        .update(dbUpdates)
        .eq("id", id)
        .eq("chatbot_id", chatbotId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge_base", chatbotId] });
      toast({
        title: "Entry updated",
        description: "Knowledge base entry has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const deleteKnowledgeBaseEntry = useMutation({
    mutationFn: async (id: string) => {
      if (!chatbotId) throw new Error("No chatbot ID provided");
      
      const { error } = await supabase
        .from("knowledge_base")
        .delete()
        .eq("id", id)
        .eq("chatbot_id", chatbotId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge_base", chatbotId] });
      toast({
        title: "Entry deleted",
        description: "Knowledge base entry has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  return {
    addKnowledgeBaseEntry,
    updateKnowledgeBaseEntry,
    deleteKnowledgeBaseEntry,
  };
};
